import styled from "styled-components";

export const PaddingContainer = styled.div`
  padding: 0 28px;
`;

export const SectionTitle = styled.div`
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  color: var(--content-default, #1e1e1e);
  margin-bottom: var(--space-100, 8px);
`;

export const CaseInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  color: var(--content-default, #1e1e1e);
`;

export const WarningContainerRect = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--space-100, 8px);
  background: var(--surface-state-warning-light, #fff0cb);
  padding: var(--space-150, 12px) var(--space-200, 16px);
  color: var(--content-state-warning-default, #9e3f00);
  border-radius: var(--radius-100, 8px);
  margin-bottom: var(--space-200, 16px);

  /* Enlarge the warning icon (its intrinsic size is 16px). */
  svg {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }
`;

export const WarningText = styled.div`
  font-family: Inter;
  font-size: 14px;
  line-height: 1.5;

  strong {
    font-weight: 600;
  }
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-150, 12px);
  margin-bottom: var(--space-200, 16px);
  flex-wrap: wrap;
  flex-shrink: 0;
`;

export const PanelTitle = styled.div`
  font-family: Inter;
  font-size: 16px;
  font-weight: 600;
  color: var(--content-default, #1e1e1e);
`;

export const FilterBar = styled.div`
  display: flex;
  gap: var(--space-100, 8px);
`;

export const FilterPill = styled.button<{ $active: boolean }>`
  cursor: pointer;
  font-family: Inter;
  font-size: 13px;
  font-weight: 500;
  padding: var(--space-050, 4px) var(--space-150, 12px);
  border-radius: 999px;
  border: 1px solid
    ${({ $active }) =>
      $active
        ? "var(--content-default, #1e1e1e)"
        : "var(--stroke-secondary, #d4dcec)"};
  background: ${({ $active }) =>
    $active
      ? "var(--content-default, #1e1e1e)"
      : "var(--surface-default, #fff)"};
  color: ${({ $active }) =>
    $active
      ? "var(--surface-default, #fff)"
      : "var(--content-secondary, #6e7686)"};
  white-space: nowrap;
`;

/* The left column is capped (via an inline max-height measured from the right form column)
   so the right column is the static anchor; the debt list fills the remaining space and
   scrolls once there are more debts than fit. */
export const DebtPanel = styled.div`
  padding: 0 28px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const DebtList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-150, 12px);
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: var(--space-050, 4px);
`;

export const Card = styled.div<{ $verified: boolean }>`
  background: var(--surface-default, #fff);
  border: 1px solid var(--stroke-secondary, #d4dcec);
  border-radius: var(--radius-100, 8px);
  overflow: hidden;
  /* Keep the natural height inside the scrollable flex list; without this the cards
     compress to fit and clip their fields. */
  flex-shrink: 0;
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-150, 12px);
  /* 28px horizontal to line up with the FormText fields below (their wrapper adds 0 28px). */
  padding: var(--space-150, 12px) 28px;
  cursor: pointer;
`;

export const CardHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-150, 12px);
  min-width: 0;
`;

export const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const CardTitle = styled.div`
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  color: var(--content-default, #1e1e1e);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CardSubtitle = styled.div`
  font-family: Inter;
  font-size: 12px;
  color: var(--content-secondary, #6e7686);
`;

export const CardHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-150, 12px);
  flex-shrink: 0;
`;

export const CardAmount = styled.div`
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  color: var(--content-default, #1e1e1e);
  white-space: nowrap;
`;

export const ChevronButton = styled.span`
  display: inline-flex;
  align-items: center;
  color: var(--content-secondary, #6e7686);
`;

export const CardBody = styled.div`
  /* No horizontal padding: the FormText wrapper provides the 0 28px inset itself. */
  padding: var(--space-050, 4px) 0 var(--space-200, 16px);
  display: flex;
  flex-direction: column;
  gap: var(--space-150, 12px);
`;

export const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-150, 12px);

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const Badge = styled.span<{ $verified: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: var(--space-050, 4px);
  font-family: Inter;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 999px;
  white-space: nowrap;
  color: ${({ $verified }) =>
    $verified
      ? "var(--content-state-success-dark, #1b5e20)"
      : "var(--content-state-warning-default, #9e3f00)"};
  background: ${({ $verified }) =>
    $verified
      ? "var(--surface-state-success-light, #e7f4e8)"
      : "var(--surface-state-warning-light, #fff0cb)"};

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

export const BadgeCheck = styled.span`
  font-family: Inter;
  font-size: 12px;
  line-height: 1;
`;

export const EmptyText = styled.div`
  font-family: Inter;
  font-size: 14px;
  color: var(--content-secondary, #6e7686);
  padding: var(--space-100, 8px) 0;
`;
